/**
 * DeltaPill — colored pill showing how much the optimizer moved an item.
 *
 * Math runs internally on the 1000‰ scale for precision, but the UI is
 * always expressed in percent (the only scale users author against — see
 * BoundsEditor, formula group weights, per-row share cells).
 *
 * For Media Devoted | Created By Ciro Autiero
 */

import { FC } from 'react';

import { cn } from '@/lib/utils';

export interface DeltaPillProps {
  deltaPerMille: number;
}

const DeltaPill: FC<DeltaPillProps> = ({ deltaPerMille }) => {
  const percent = deltaPerMille / 10;
  // Snap any sub-tenth-of-a-percent moves to zero so we don't draw a green
  // pill for what is really a rounding artefact.
  if (Math.abs(percent) < 0.05) {
    return (
      <span className="inline-flex items-center rounded-full border border-border bg-muted/40 px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">
        0%
      </span>
    );
  }
  const positive = percent > 0;
  return (
    <span
      className={cn(
        'inline-flex items-center gap-0.5 rounded-full border px-1.5 py-0.5 text-[10px] font-semibold tabular-nums',
        positive
          ? 'border-emerald-400/40 bg-emerald-500/10 text-emerald-600'
          : 'border-destructive/40 bg-destructive/10 text-destructive',
      )}
    >
      {positive ? '+' : ''}
      {percent.toFixed(1)}%
    </span>
  );
};

export default DeltaPill;
